/**
 * Central date formatting and parsing using date-fns only.
 * Use these helpers everywhere so behavior is consistent and SSR-safe
 * (no native Date string parsing or toLocaleDateString).
 */
import { format, parseISO } from 'date-fns';

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a value (API date string or Date) to a Date.
 * For YYYY-MM-DD strings, parses as local date to avoid UTC shift.
 * @param {string|Date} value - API often returns 'YYYY-MM-DD' or ISO string
 * @returns {Date}
 */
export function parseDateSafe(value) {
  if (value == null) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'string' && DATE_ONLY_REGEX.test(value.trim())) {
    const [y, m, d] = value.trim().split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  if (typeof value === 'string') return parseISO(value);
  return new Date(value);
}

/**
 * Format for <input type="date"> (yyyy-MM-dd).
 */
export function formatForInput(date) {
  return format(parseDateSafe(date), 'yyyy-MM-dd');
}

/**
 * Display format: dd/MM/yyyy (e.g. tables, short).
 */
export function formatDisplayShort(date) {
  return format(parseDateSafe(date), 'dd/MM/yyyy');
}

/**
 * Display format: dd/MM/yyyy (same as short; used for reports, history).
 */
export function formatDisplayLong(date) {
  return format(parseDateSafe(date), 'dd/MM/yyyy');
}

/**
 * Today as yyyy-MM-dd for default form values.
 */
export function todayForInput() {
  return formatForInput(new Date());
}

/**
 * Display format with time: dd/MM/yyyy HH:mm (e.g. feedback, activity lists).
 */
export function formatDisplayDateTime(date) {
  return format(parseDateSafe(date), 'dd/MM/yyyy HH:mm');
}

/**
 * Time-of-day for a timestamp, rendered in the browser's local timezone
 * (e.g. '9:15am'). Accepts a unix-ms number, Date, or ISO/timestamptz string.
 * Use this for timer attribution so DST and QLD/NSW border offsets are correct.
 */
export function formatDisplayTime(value) {
  return format(parseDateSafe(value), 'h:mmaaa');
}

/**
 * Fixed civil-time zone for printed compliance-report timestamps (e.g. the
 * Test Results 'Start' column) — deliberately NOT the browser's own
 * timezone, so the report prints the same time regardless of where it is
 * generated (Brief 17 §1b: the renderer's PDF pins the same zone in
 * `sectionBuilder.js`'s own `REPORT_TIMEZONE`, on purpose — the two must
 * agree by construction, not by the coincidence of both machines being in
 * Queensland). Brisbane specifically, not Sydney: Queensland does not
 * observe daylight saving and Sydney does, so a Sydney-based format would be
 * wrong for half the year. There is no per-organisation timezone field to
 * read from; if one is added later, this is the single place to change.
 */
const REPORT_TIMEZONE = 'Australia/Brisbane';

/**
 * Time-of-day (24-hour HH:mm) for a timestamp, always in REPORT_TIMEZONE —
 * for compliance report printing, as opposed to `formatDisplayTime`'s
 * browser-local time used for in-app display. Returns '' when there is no
 * valid time component (a date-only value, or an unparseable one).
 *
 * Uses `Intl.DateTimeFormat` directly rather than date-fns: date-fns itself
 * has no timezone support without the separate `date-fns-tz` package, and
 * this file is Rule 1's one permitted exception for exactly that reason.
 */
export function formatReportTime(value) {
  if (value == null || value === '') return '';
  const str = String(value);
  if (!str.includes('T') && !str.includes(' ')) return '';

  const date = parseDateSafe(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: REPORT_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  return hour && minute ? `${hour}:${minute}` : '';
}
